import { useEffect, useState } from "react";
import {
  areFeatureFlagsLoaded,
  getPostHogClient,
  hasPostHogKey,
  identifyPostHogUser,
  initPostHog,
  isPostHogInitialized,
  resetPostHogUser,
  subscribeToFeatureFlags,
} from "./posthogClient";

/**
 * Feature flag utilities using PostHog
 */

const isDev = import.meta.env.DEV;

export { initPostHog };

/**
 * React hook to track whether feature flags are ready
 */
export const useFeatureFlagsReady = (): boolean => {
  const [ready, setReady] = useState(
    () => !hasPostHogKey || !isPostHogInitialized() || areFeatureFlagsLoaded()
  );

  useEffect(() => {
    const update = () => {
      setReady(
        !hasPostHogKey || !isPostHogInitialized() || areFeatureFlagsLoaded()
      );
    };
    const unsubscribe = subscribeToFeatureFlags(update);
    update();
    return unsubscribe;
  }, []);

  return ready;
};

/**
 * Identify a user for feature flag targeting
 */
export const identifyUser = (
  userId: string,
  properties?: Record<string, unknown>
): void => {
  identifyPostHogUser(userId, properties);
};

/**
 * Reset user identity (on logout)
 */
export const resetUser = (): void => {
  resetPostHogUser();
};

/**
 * Check if a feature flag is enabled
 */
export const isFeatureEnabled = (flagKey: string, defaultValue = false): boolean => {
  const posthog = getPostHogClient();
  if (!posthog) return defaultValue;
  return posthog.isFeatureEnabled(flagKey) ?? defaultValue;
};

/**
 * Get feature flag value (for multivariate flags)
 */
export const getFeatureFlagValue = (
  flagKey: string
): string | boolean | undefined => {
  const posthog = getPostHogClient();
  if (!posthog) return undefined;
  return posthog.getFeatureFlag(flagKey);
};

/**
 * React hook for feature flags
 * Re-renders when flags are loaded/changed
 */
export const useFeatureFlag = (flagKey: string, defaultValue = false): boolean => {
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flagKey, defaultValue));

  useEffect(() => {
    if (!isPostHogInitialized()) {
      if (isDev) console.info(`[PostHog] Not initialized, flag "${flagKey}" defaulting to false`);
    }

    const handleFlagsLoaded = () => {
      const value = isFeatureEnabled(flagKey, defaultValue);
      if (isDev) console.info(`[PostHog] Flags loaded, "${flagKey}" = ${value}`);
      setEnabled(value);
    };

    const unsubscribe = subscribeToFeatureFlags(handleFlagsLoaded);
    const currentValue = isFeatureEnabled(flagKey, defaultValue);
    if (isDev) console.info(`[PostHog] Initial check, "${flagKey}" = ${currentValue}`);
    setEnabled(currentValue);

    return unsubscribe;
  }, [flagKey, defaultValue]);

  return enabled;
};

/**
 * React hook for multivariate feature flags
 */
export const useFeatureFlagValue = (
  flagKey: string
): string | boolean | undefined => {
  const [value, setValue] = useState(() => getFeatureFlagValue(flagKey));

  useEffect(() => {
    const handleFlagsLoaded = () => {
      setValue(getFeatureFlagValue(flagKey));
    };

    const unsubscribe = subscribeToFeatureFlags(handleFlagsLoaded);
    setValue(getFeatureFlagValue(flagKey));
    return unsubscribe;
  }, [flagKey]);

  return value;
};

/**
 * Feature flag keys used in the app
 * Use environment-specific keys for local development
 */
const getEnvFlagKey = (baseName: string): string => {
  const isLocal = import.meta.env.DEV;
  return isLocal ? `${baseName}-local` : baseName;
};

export const FEATURE_FLAGS = {
  FEATURED_DESTINATIONS_V2: getEnvFlagKey("featured-destinations-v2"),
  HOTELS_XENI_ENABLED: getEnvFlagKey("hotels-xeni-enabled"),
  SPORTS_CALENDAR_ENABLED: getEnvFlagKey("sports-calendar-v1"),
  POINTS_DASHBOARD_ENABLED: getEnvFlagKey("points-dashboard-v1"),
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];
